// src/components/ui/2-organisms/VideoSection/VideoSection.js

import React, { useState, useRef, useEffect, useMemo } from 'react';
import Icon from '../../1-molecules/Icon/Icon';
import VideoModal from '../VideoModal/VideoModal';
import styles from './VideoSection.module.css';
import { getOptimizedImageUrl } from '../../../../utils/imageUtils';

/**
 * Video section component with thumbnail and modal player
 * Priority: 1) Stored thumbnail, 2) Generated from video, 3) Fallback placeholder
 * @param {string} videoUrl - URL of the video from Firebase
 * @param {string} [thumbnailUrl] - URL of the thumbnail image (stored in Firebase)
 * @param {string} [title] - Video title
 * @param {string} [className] - Additional CSS classes
 */
const VideoSection = ({ 
  videoUrl, 
  thumbnailUrl,
  title = "Jeweller's showcase",
  className = '',
  disablePlay = false
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [videoThumbnail, setVideoThumbnail] = useState(null);
  const [thumbnailLoading, setThumbnailLoading] = useState(true);
  const [generationFailed, setGenerationFailed] = useState(false);
  const videoPreviewRef = useRef(null);
  const generationTimeoutRef = useRef(null);

  const optimizedStoredThumbnail = useMemo(() => {
    if (!thumbnailUrl) return null;
    return getOptimizedImageUrl(thumbnailUrl, 'MEDIUM');
  }, [thumbnailUrl]);

  useEffect(() => {
    // PRIORITY 1: If we have a stored thumbnail URL, use it (best option)
    if (optimizedStoredThumbnail) {
      console.log('Using stored thumbnail');
      setVideoThumbnail(optimizedStoredThumbnail);
      setThumbnailLoading(false);
      setGenerationFailed(false);
      return;
    }

    // PRIORITY 2: Try to generate thumbnail from video (fallback for old videos)
    console.log('No stored thumbnail, attempting client-side generation...');
    
    if (videoUrl && videoPreviewRef.current) {
      const video = videoPreviewRef.current;
      
      // Set a timeout - if generation takes more than 10 seconds, give up
      generationTimeoutRef.current = setTimeout(() => {
        console.warn('Thumbnail generation timeout - using fallback');
        setGenerationFailed(true);
        setThumbnailLoading(false);
      }, 10000);
      
      const handleLoadedData = () => {
        console.log('Video metadata loaded, seeking to frame...');
        // Seek to 1 second to get a better frame (not always black)
        video.currentTime = 1;
      };

      const handleSeeked = () => {
        try {
          console.log('Generating thumbnail from video frame...');
          // Create a canvas to capture the frame
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          // Convert to data URL and set as thumbnail
          const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
          setVideoThumbnail(dataUrl);
          setThumbnailLoading(false);
          setGenerationFailed(false);
          console.log('Thumbnail generated successfully');
          
          // Clear timeout since we succeeded
          if (generationTimeoutRef.current) {
            clearTimeout(generationTimeoutRef.current);
          }
        } catch (error) {
          console.error('Failed to generate thumbnail:', error);
          setGenerationFailed(true);
          setThumbnailLoading(false);
        }
      };

      const handleError = (e) => {
        console.error('Video loading error:', e);
        setGenerationFailed(true);
        setThumbnailLoading(false);
        if (generationTimeoutRef.current) {
          clearTimeout(generationTimeoutRef.current);
        }
      };

      video.addEventListener('loadeddata', handleLoadedData);
      video.addEventListener('seeked', handleSeeked);
      video.addEventListener('error', handleError);
      
      // Start loading the video
      video.load();

      return () => {
        video.removeEventListener('loadeddata', handleLoadedData);
        video.removeEventListener('seeked', handleSeeked);
        video.removeEventListener('error', handleError);
        if (generationTimeoutRef.current) {
          clearTimeout(generationTimeoutRef.current);
        }
      };
    } else {
      // No video to generate from
      setGenerationFailed(true);
      setThumbnailLoading(false);
    }
  }, [videoUrl, optimizedStoredThumbnail]);

  // Don't render if there's no video URL
  if (!videoUrl) {
    return null;
  }

  const handlePlay = () => {
    if (disablePlay) return;
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  // PRIORITY 3: Fallback thumbnail (if no stored thumbnail and generation failed)
  const fallbackThumbnail = 'https://images.unsplash.com/photo-1599643477877-53a883583583?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&h=450&q=80';

  // Determine which thumbnail to show
  const displayThumbnail = videoThumbnail || fallbackThumbnail;
  const shouldShowLoading = thumbnailLoading && !generationFailed;

  return (
    <>
      <section className={`${styles.section} ${className}`}>
        <div
          className={styles.videoContainer}
          onClick={handlePlay}
          role={disablePlay ? 'presentation' : 'button'}
          aria-disabled={disablePlay}
        >
          {/* Hidden video element for thumbnail extraction (only if no stored thumbnail) */}
          {!optimizedStoredThumbnail && !generationFailed && (
            <video
              ref={videoPreviewRef}
              src={videoUrl}
              className={styles.hiddenVideo}
              muted
              playsInline
              crossOrigin="anonymous"
            />
          )}
          
          {/* Display thumbnail */}
          <img 
            src={displayThumbnail} 
            alt={title}
            className={styles.thumbnail}
            style={{ opacity: shouldShowLoading ? 0 : 1 }}
          />
          
          {/* Loading state */}
          {shouldShowLoading && (
            <div className={styles.loadingPlaceholder}>
              <div className={styles.shimmer} />
            </div>
          )}
          
          {/* Overlay and play button */}
          <div className={styles.overlay} />
          {!disablePlay && (
            <div className={styles.playButton}>
              <Icon name="play-gradient" size="l" />
            </div>
          )}
        </div>
      </section>

      {!disablePlay && (
        <VideoModal
          videoUrl={videoUrl}
          isOpen={isModalOpen}
          onClose={handleClose}
          title={title}
        />
      )}
    </>
  );
};

export default VideoSection;
